import { FC, JSX } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import Radio, { RadioGroup } from '@semcore/ui/radio';

import { ContentStyles } from '@/entities/content/api/types';
import Input from '@/shared/ui/Input';

interface Props {}

const textBlocksAnimationOptions: {
  value: ContentStyles['config']['text_blocks_animation']['style'];
  label: string;
}[] = [
  { value: 'horizontal', label: 'Horizontal' },
  { value: 'vertical', label: 'Vertical' },
  { value: 'fade', label: 'Fade' },
];

const slidesAnimationOptions: {
  value: ContentStyles['config']['slides_animation']['style'];
  label: string;
}[] = [
  { value: 'fade-in', label: 'Fade In' },
  { value: 'zoom-in', label: 'Zoom In' },
];

const AnimationStyleSettings: FC<Props> = (): JSX.Element => {
  const { control, watch, register } = useFormContext<ContentStyles>();
  const contentType = watch('type');

  return (
    <>
      {/* Video: text_blocks_animation */}
      <div
        className={contentType === 'video' ? 'flex flex-col gap-2' : 'hidden'}
      >
        <div className="flex">
          <div className="flex grow text-sm font-semibold">Animation style</div>
        </div>
        <Controller
          control={control}
          name="config.text_blocks_animation.style"
          render={({ field: { value, onChange: controllerOnChange } }) => (
            <RadioGroup
              name="text-blocks-animation-style"
              value={value ?? 'horizontal'}
              onChange={(val: unknown) => controllerOnChange(val as string)}
            >
              <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                {textBlocksAnimationOptions.map((opt) => (
                  <div className="py-1" key={opt.value}>
                    <Radio
                      value={opt.value}
                      label={opt.label}
                      size="l"
                      className="text-base font-light leading-4"
                    />
                  </div>
                ))}
              </div>
            </RadioGroup>
          )}
        />
      </div>

      {/* Slider: slides_animation */}
      <div
        className={contentType === 'slider' ? 'flex flex-col gap-2' : 'hidden'}
      >
        <div className="flex">
          <div className="flex grow text-sm font-semibold">Animation style</div>
        </div>
        <Controller
          control={control}
          name="config.slides_animation.style"
          render={({ field: { value, onChange: controllerOnChange } }) => (
            <RadioGroup
              name="slides-animation-style"
              value={value ?? 'fade-in'}
              onChange={(val: unknown) => controllerOnChange(val as string)}
            >
              <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                {slidesAnimationOptions.map((opt) => (
                  <div className="py-1" key={opt.value}>
                    <Radio
                      value={opt.value}
                      label={opt.label}
                      size="l"
                      className="text-base font-light leading-4"
                    />
                  </div>
                ))}
              </div>
            </RadioGroup>
          )}
        />

        <div className="flex flex-col gap-1 pt-2">
          <div className="text-sm font-semibold">Slide duration (seconds)</div>
          <Input
            type="number"
            min={1}
            placeholder="Duration"
            {...register('config.slides_animation.duration', {
              valueAsNumber: true,
            })}
          />
        </div>
      </div>
    </>
  );
};

export default AnimationStyleSettings;
